import type { Residence } from '@repo/sdk/gen'
import {
	SERVICE_GROUPS,
	SERVICE_ICON,
	serviceGroupKey,
	serviceLabelKey
} from '@repo/ui/services'
import { cn } from '@repo/ui/utils'
import { Check } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from '@/lib/i18n'

const ALL = SERVICE_GROUPS.flatMap((group) => group.services)

/**
 * What the residence offers, as chips to tick: an icon and the name of each
 * service, one row per group. Chips wrap on their own, so a short group leaves
 * no empty cells. The list is closed: the student app shows the same icon and
 * name for every residence.
 */
function ServicePicker() {
	const { t } = useTranslation()
	const { control } = useFormContext<Residence>()

	return (
		<Controller
			control={control}
			name='services'
			render={({ field }) => (
				<div className='flex flex-col gap-2 md:col-span-2'>
					{SERVICE_GROUPS.map((group) => (
						<fieldset
							className='m-0 min-w-0 border-0 p-0'
							key={group.key}
						>
							{/* The groups keep their order and their name for screen readers, without a heading on screen. */}
							<legend className='sr-only'>
								{t(serviceGroupKey(group.key))}
							</legend>
							<div className='flex flex-wrap gap-2'>
								{group.services.map((service) => {
									const checked =
										field.value.includes(service)
									const Icon = SERVICE_ICON[service]
									return (
										<label
											className={cn(
												'flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl border bg-card pr-3 pl-3 text-sm transition-colors hover:bg-accent/60',
												'has-checked:border-primary has-checked:bg-accent has-focus-visible:ring-3 has-focus-visible:ring-ring/50',
												'has-disabled:cursor-not-allowed has-disabled:opacity-60'
											)}
											key={service}
										>
											<input
												checked={checked}
												className='sr-only'
												disabled={field.disabled}
												onChange={(event) => {
													const next = new Set(
														field.value
													)
													if (event.target.checked) {
														next.add(service)
													} else {
														next.delete(service)
													}
													field.onChange(
														ALL.filter((code) =>
															next.has(code)
														)
													)
												}}
												type='checkbox'
											/>
											<Icon
												aria-hidden
												className={cn(
													'size-5 shrink-0',
													checked
														? 'text-primary'
														: 'text-muted-foreground'
												)}
											/>
											{t(serviceLabelKey(service))}
											<Check
												aria-hidden
												className={cn(
													'size-4 shrink-0 text-primary',
													!checked && 'hidden'
												)}
											/>
										</label>
									)
								})}
							</div>
						</fieldset>
					))}
				</div>
			)}
		/>
	)
}

export { ServicePicker }
